import {
  Badge,
  Box,
  Link,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react'
import { openLinkInNewTab } from '@utilities'
import React, { useEffect, useState } from 'react'
import type { SessionUsageInvoiceDTO } from 'src/core/api/__generated__/clinician-api/models/sessionUsageInvoiceDTO'
import {
  useOrganizationControllerGetInvoices,
  useUsageBillingInvoiceControllerFindById
} from '~/clinician-api'

export const InvoicesTable = ({
  organizationId
}: {
  organizationId: string
}) => {
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string>('')
  const { data: invoices, isLoading } =
    useOrganizationControllerGetInvoices(organizationId)

  const { data: invoice } = useUsageBillingInvoiceControllerFindById(
    selectedInvoiceId,
    {
      query: {
        enabled: !!selectedInvoiceId
      }
    }
  )

  useEffect(() => {
    if (invoice) {
      openLinkInNewTab(invoice.invoice_pdf)
    }
  }, [invoice])

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const formatAmount = (amount: number) => {
    return `$${(amount / 100).toFixed(2)}`
  }

  const getPlanDisplayName = (planType: string, numberOfSessions: number) => {
    const planName = planType === 'plus' ? 'Plus' : 'Standard'
    return `${planName} - ${numberOfSessions} Sessions`
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <Spinner size="lg" />
      </Box>
    )
  }

  return (
    <Box>
      <TableContainer>
        <Table
          variant="simple"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="4px"
          style={{
            borderCollapse: 'separate',
            width: '100%'
          }}
        >
          <Thead>
            <Tr>
              <Th
                textAlign="left"
                fontWeight="semibold"
                color="gray.700"
                borderBottom="1px solid"
                borderColor="gray.200"
                textTransform="none"
                py={3}
                px={4}
              >
                Date
              </Th>
              <Th
                textAlign="left"
                fontWeight="semibold"
                color="gray.700"
                borderBottom="1px solid"
                borderColor="gray.200"
                textTransform="none"
                py={3}
                px={4}
              >
                Description
              </Th>
              <Th
                textAlign="right"
                fontWeight="semibold"
                color="gray.700"
                borderBottom="1px solid"
                borderColor="gray.200"
                textTransform="none"
                py={3}
                px={4}
              >
                Amount
              </Th>
              <Th
                textAlign="center"
                fontWeight="semibold"
                color="gray.700"
                borderBottom="1px solid"
                borderColor="gray.200"
                textTransform="none"
                py={3}
                px={4}
              >
                Status
              </Th>
              <Th
                textAlign="left"
                fontWeight="semibold"
                color="gray.700"
                borderBottom="1px solid"
                borderColor="gray.200"
                textTransform="none"
                py={3}
                px={4}
              >
                Invoice
              </Th>
            </Tr>
          </Thead>
          <Tbody
            sx={{
              'tr:last-child td': {
                borderBottom: 'none'
              }
            }}
          >
            {!invoices || invoices.length === 0 ? (
              <Tr>
                <Td
                  colSpan={5}
                  textAlign="center"
                  borderBottom="1px solid"
                  borderColor="gray.200"
                  py={8}
                  px={4}
                >
                  <Text color="gray.500">No invoices found</Text>
                </Td>
              </Tr>
            ) : (
              invoices.map((invoice: SessionUsageInvoiceDTO) => (
                <Tr key={invoice.id}>
                  <Td
                    textAlign="left"
                    borderBottom="1px solid"
                    borderColor="gray.200"
                    py={3}
                    px={4}
                  >
                    {formatDate(invoice.createdAt)}
                  </Td>
                  <Td
                    textAlign="left"
                    borderBottom="1px solid"
                    borderColor="gray.200"
                    py={3}
                    px={4}
                  >
                    {getPlanDisplayName(
                      invoice.planType,
                      invoice.numberOfSessions
                    )}
                  </Td>
                  <Td
                    textAlign="right"
                    borderBottom="1px solid"
                    borderColor="gray.200"
                    py={3}
                    px={4}
                  >
                    {formatAmount(invoice.amountPaid)}
                  </Td>
                  <Td
                    textAlign="center"
                    borderBottom="1px solid"
                    borderColor="gray.200"
                    py={3}
                    px={4}
                  >
                    <Badge
                      colorScheme="green"
                      variant="solid"
                      fontSize="12px"
                      color="green.800"
                    >
                      PAID
                    </Badge>
                  </Td>
                  <Td
                    textAlign="left"
                    borderBottom="1px solid"
                    borderColor="gray.200"
                    py={3}
                    px={4}
                  >
                    <Link
                      color="primary"
                      cursor="pointer"
                      onClick={() =>
                        setSelectedInvoiceId(invoice.stripeInvoiceId)
                      }
                    >
                      Download
                    </Link>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  )
}
